import { skipToken, useQuery } from "@tanstack/react-query";

import instance from "@/lib/axios";
import { useOrganizationStore } from "@/stores/useOrganizationStore";
import type {
  NotificationAudience,
  UnreadCountResponse,
} from "@/types/notification";

import { notificationKeys } from "./keys";

async function fetchUnreadCount(
  orgSlug: string,
  audience?: NotificationAudience,
): Promise<UnreadCountResponse> {
  const response = await instance.get<UnreadCountResponse>(
    `/api/${orgSlug}/notifications/unread-count/`,
    { params: audience ? { audience } : undefined },
  );
  return response.data;
}

/**
 * Cheap unread badge count. No interval polling (by design) — refreshes on
 * mount, on window focus (React Query default), and after mark-read mutations
 * invalidate the key. Pass `audience` to scope the count to one role area.
 */
export function useUnreadCount(
  audience?: NotificationAudience,
  options?: { enabled?: boolean },
) {
  const orgSlug = useOrganizationStore((s) => s.orgSlug);
  return useQuery({
    queryKey: [...notificationKeys.unreadCount(audience), orgSlug],
    queryFn: orgSlug ? () => fetchUnreadCount(orgSlug, audience) : skipToken,
    enabled: options?.enabled ?? true,
    meta: { errorMessage: false },
  });
}

export default useUnreadCount;
